import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Lock,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Shield,
  Users,
  LogOut,
  Loader2,
  CheckCircle2,
  Mail,
  FileText,
  Video,
  PlayCircle,
  Download,
  ChevronRight,
  Star,
  Award,
  Circle,
  X,
  Sparkles,
} from "lucide-react";
import Footer from "@/components/landing/Footer";
import CookieConsent from "@/components/landing/CookieConsent";
import PayPalPayButton from "@/components/payments/PayPalPayButton";
import ChatWidget from "@/components/chat/ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mdm_member_token";

const getMemberToken = () => localStorage.getItem(TOKEN_KEY);
const setMemberToken = (t) =>
  t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
const memberHdr = () => {
  const t = getMemberToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ICONS = {
  "network-marketing": Users,
  leadership: BookOpen,
  "tutela-digitale": Shield,
};

export default function AreaIscrittiPage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ page: "dashboard", slug: null }); // dashboard | course

  useEffect(() => {
    (async () => {
      if (!getMemberToken()) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/member/me`, { headers: memberHdr() });
        setMe(data);
      } catch {
        setMemberToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0B192C]" size={28} />
      </main>
    );
  }

  return (
    <main data-testid="area-iscritti-page" className="min-h-screen bg-[#F9F8F6] text-[#0B192C]">
      <header className="border-b border-[#E5E1DA] bg-[#FFFFFF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="font-serif-display text-xl tracking-tight">
            Metodo<span className="text-[#E89B6C]">·</span>di<span className="text-[#E89B6C]">·</span>Mare
          </Link>
          <div className="flex items-center gap-4">
            {me ? (
              <>
                <span className="hidden md:inline text-xs uppercase tracking-[0.18em] text-[#3A4F6B]">
                  {me.email}
                </span>
                <button
                  onClick={() => {
                    setMemberToken(null);
                    window.location.reload();
                  }}
                  data-testid="member-logout-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors"
                >
                  <LogOut size={13} /> Esci
                </button>
              </>
            ) : (
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#0E7C8F]"
              >
                <ArrowLeft size={14} /> Torna al sito
              </Link>
            )}
          </div>
        </div>
      </header>

      {!me ? (
        <LoginView />
      ) : view.page === "course" ? (
        <CourseDetailView slug={view.slug} onBack={() => setView({ page: "dashboard" })} />
      ) : (
        <DashboardView me={me} onOpenCourse={(slug) => setView({ page: "course", slug })} />
      )}

      <Footer />
      <CookieConsent />
      <ChatWidget persona="member" authToken={getMemberToken()} position="bottom-right" />
    </main>
  );
}

// ============================================================
// LOGIN
// ============================================================

function LoginView() {
  const [mode, setMode] = useState("login"); // login | request | register
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [error, setError] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/member/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      setMemberToken(data.access_token);
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore di rete.");
    } finally {
      setLoading(false);
    }
  };

  const requestCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/member/request-credentials`, {
        email: email.trim().toLowerCase(),
      });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore di rete.");
    } finally {
      setLoading(false);
    }
  };

  const register = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/member/register`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        consent: true,
      });
      setRegistered(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore di rete.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6]">
      <div className="max-w-md mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-6">
            <Lock size={14} className="text-[#E89B6C]" />
            <span className="overline text-[#E89B6C]">Area iscritti</span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight">
            Accedi alla tua <em className="text-[#E89B6C]">rotta personale.</em>
          </h1>
        </div>
        {sent ? (
          <div data-testid="creds-sent-msg" className="bg-[#E89B6C]/15 border border-[#E89B6C] p-8 text-center">
            <Mail size={32} className="text-[#E89B6C] mx-auto mb-4" />
            <h3 className="font-serif-display text-2xl text-[#F9F8F6] mb-3">Controlla la tua casella</h3>
            <p className="text-sm font-light text-[#F9F8F6]/80 leading-relaxed">
              Se la tua email risulta iscritta, ti ho mandato user e password a <strong>{email}</strong>. Controlla anche lo spam, poi torna qui per fare login.
            </p>
            <button
              onClick={() => {
                setSent(false);
                setMode("login");
              }}
              className="mt-6 text-xs uppercase tracking-[0.2em] text-[#E89B6C] hover:text-[#F9F8F6] link-underline"
            >
              Torna al login
            </button>
          </div>
        ) : mode === "login" ? (
          <form onSubmit={login} className="bg-[#F9F8F6]/8 backdrop-blur-md border border-[#F9F8F6]/15 p-8 space-y-5">
            <div>
              <label className="overline block mb-2 text-[#F9F8F6]/80">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="member-email-input"
                className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-base font-light text-[#F9F8F6] focus:outline-none focus:border-b-2 focus:border-[#E89B6C]"
                placeholder="la-tua@email.it"
              />
            </div>
            <div>
              <label className="overline block mb-2 text-[#F9F8F6]/80">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="member-password-input"
                className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-base font-light text-[#F9F8F6] focus:outline-none focus:border-b-2 focus:border-[#E89B6C]"
                placeholder={String.fromCharCode(8226).repeat(8)}
              />
            </div>
            {error && (
              <p data-testid="member-login-error" className="text-sm text-[#FF8B6E]">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="member-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Accesso...
                </>
              ) : (
                <>
                  <Lock size={14} /> Accedi <ArrowRight size={14} />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("request");
                setError("");
              }}
              data-testid="request-creds-link"
              className="w-full text-center text-xs uppercase tracking-[0.18em] text-[#F9F8F6]/70 hover:text-[#E89B6C] link-underline pt-2"
            >
              Non ricordi le credenziali? Richiedile via email
            </button>
            <div className="pt-4 border-t border-[#F9F8F6]/15 text-center">
              <p className="text-xs text-[#F9F8F6]/60 mb-2">Non hai ancora un account?</p>
              <button
                type="button"
                onClick={() => {
                  setMode("register");
                  setError("");
                }}
                data-testid="register-link"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#E89B6C] text-[#E89B6C] text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] hover:text-[#0B192C] transition-all"
              >
                Registrati gratis
              </button>
            </div>
          </form>
        ) : mode === "register" ? (
          registered ? (
            <div data-testid="register-success" className="bg-[#F9F8F6]/8 backdrop-blur-md border border-[#F9F8F6]/15 p-10 text-center">
              <CheckCircle2 size={40} className="text-[#E89B6C] mx-auto mb-4" />
              <h3 className="font-serif-display text-2xl mb-3">Registrazione ricevuta</h3>
              <p className="text-sm font-light text-[#F9F8F6]/80 leading-relaxed">
                Grazie {name.split(" ")[0]}! La tua richiesta è in attesa di approvazione manuale.
                Riceverai un&apos;email con le credenziali di accesso non appena Michele avrà
                approvato il tuo account (di solito entro 24 ore).
              </p>
              <p className="mt-4 text-xs text-[#F9F8F6]/60">
                Controlla anche la cartella <strong>Spam</strong> se non vedi l&apos;email.
              </p>
              <button
                onClick={() => { setRegistered(false); setMode("login"); setName(""); }}
                className="mt-6 text-xs uppercase tracking-[0.2em] text-[#E89B6C] hover:text-[#F9F8F6] link-underline"
              >
                Torna al login
              </button>
            </div>
          ) : (
            <form onSubmit={register} className="bg-[#F9F8F6]/8 backdrop-blur-md border border-[#F9F8F6]/15 p-8 space-y-5" data-testid="register-form">
              <p className="text-sm font-light text-[#F9F8F6]/80">
                Registrati gratis per accedere all&apos;Area Iscritti. La tua richiesta verrà
                approvata manualmente e riceverai le credenziali via email.
              </p>
              <div>
                <label className="overline block mb-2 text-[#F9F8F6]/80">Nome e cognome</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input"
                  className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-base font-light text-[#F9F8F6] focus:outline-none focus:border-b-2 focus:border-[#E89B6C]"
                  placeholder="Mario Rossi"
                />
              </div>
              <div>
                <label className="overline block mb-2 text-[#F9F8F6]/80">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="register-email-input"
                  className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-base font-light text-[#F9F8F6] focus:outline-none focus:border-b-2 focus:border-[#E89B6C]"
                  placeholder="la-tua@email.it"
                />
              </div>
              {error && <p className="text-sm text-[#FF8B6E]" data-testid="register-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                data-testid="register-submit"
                className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all disabled:opacity-60"
              >
                {loading ? (
                  <><Loader2 size={14} className="animate-spin" /> Invio...</>
                ) : (
                  <>Registrami <ArrowRight size={14} /></>
                )}
              </button>
              <p className="text-[10px] text-[#F9F8F6]/50 leading-relaxed pt-2">
                Registrandoti accetti la <a href="/privacy" className="underline">Privacy Policy</a> e i
                <a href="/termini" className="underline"> Termini di Servizio</a>. Le tue credenziali
                verranno inviate all&apos;email indicata dopo l&apos;approvazione manuale.
              </p>
              <button
                type="button"
                onClick={() => { setMode("login"); setError(""); }}
                className="w-full text-center text-xs uppercase tracking-[0.18em] text-[#F9F8F6]/70 hover:text-[#E89B6C] link-underline pt-2"
              >
                Ho già un account · Accedi
              </button>
            </form>
          )
        ) : (
          <form onSubmit={requestCreds} className="bg-[#F9F8F6]/8 backdrop-blur-md border border-[#F9F8F6]/15 p-8 space-y-5">
            <p className="text-sm font-light text-[#F9F8F6]/80">
              Inserisci la tua email: ti invio subito email e password per accedere.
            </p>
            <div>
              <label className="overline block mb-2 text-[#F9F8F6]/80">La tua email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="request-creds-input"
                className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-base font-light text-[#F9F8F6] focus:outline-none focus:border-b-2 focus:border-[#E89B6C]"
                placeholder="la-tua@email.it"
              />
            </div>
            {error && <p className="text-sm text-[#FF8B6E]">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="request-creds-submit"
              className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Invio...
                </>
              ) : (
                <>
                  <Mail size={14} /> Inviami le credenziali
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setMode("login")}
              className="w-full text-center text-xs uppercase tracking-[0.18em] text-[#F9F8F6]/70 hover:text-[#E89B6C] link-underline pt-2"
            >
              Torna al login
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// ============================================================
// DASHBOARD — list courses
// ============================================================

function DashboardView({ me, onOpenCourse }) {
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/member/courses`, { headers: memberHdr() }),
          axios.get(`${API}/member/progress`, { headers: memberHdr() }),
        ]);
        setCourses(c.data.courses || []);
        setProgress(p.data.progress || {});
      } catch {
        setCourses([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Find the most recent in-progress course for a "Resume" banner
  const resumeCourse = (() => {
    let best = null;
    for (const c of courses) {
      const pr = progress[c.slug];
      if (!pr || pr.percent === 0 || pr.percent === 100) continue;
      if (!best || (pr.updated_at && pr.updated_at > (progress[best.slug]?.updated_at || ""))) best = c;
    }
    return best;
  })();

  return (
    <>
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <p className="overline text-[#E89B6C]">
            Benvenuto a bordo{me.name ? `, ${me.name}` : ""}
          </p>
          <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            La tua <em className="text-[#E89B6C]">rotta</em> personale.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg font-light text-[#F9F8F6]/80">
            Corsi pratici per applicare il Metodo di Mare al tuo business digitale.
            Studia quando vuoi, dove vuoi.
          </p>
          {resumeCourse && (
            <div data-testid="resume-banner" className="mt-10 inline-flex flex-col md:flex-row md:items-center gap-5 bg-[#F9F8F6]/8 border border-[#E89B6C]/40 backdrop-blur-md px-6 py-5">
              <div>
                <p className="overline text-[#E89B6C]">Riprendi da dove hai lasciato</p>
                <p className="font-serif-display text-xl mt-1">{resumeCourse.title}</p>
                <p className="text-xs font-light text-[#F9F8F6]/70 mt-1">
                  {progress[resumeCourse.slug]?.percent || 0}% completato
                </p>
              </div>
              <button
                onClick={() => onOpenCourse(resumeCourse.slug)}
                data-testid="resume-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-colors"
              >
                Continua <ArrowRight size={13} />
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex items-center gap-3 text-[#3A4F6B]">
              <Loader2 className="animate-spin" size={18} /> Caricamento corsi...
            </div>
          ) : courses.length === 0 ? (
            <div className="border border-dashed border-[#E5E1DA] py-20 text-center text-sm text-[#3A4F6B]">
              Nessun corso disponibile al momento. Ricevi un&apos;email quando saranno pronti.
            </div>
          ) : (
            <>
              {/* Subscription CTA banner */}
              <SubscriptionBanner courses={courses} />
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((c) => {
                const Icon = ICONS[c.slug] || BookOpen;
                const isAvail = c.status === "available";
                const pr = progress[c.slug];
                const pct = pr?.percent || 0;
                const completed = pct === 100;
                const isPremium = c.is_premium;
                const hasAccess = c.has_access !== false;
                const clickable = isAvail && (hasAccess || isPremium); // premium clickable to see preview + buy
                return (
                  <article
                    key={c.slug}
                    data-testid={`member-course-${c.slug}`}
                    className={`bg-[#FFFFFF] border border-[#E5E1DA] flex flex-col group relative overflow-hidden ${clickable ? "cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300" : ""}`}
                    onClick={() => clickable && onOpenCourse(c.slug)}
                  >
                    {/* Cover image */}
                    <div className="relative aspect-video bg-[#0B192C] overflow-hidden">
                      {c.cover_image ? (
                        <img
                          src={c.cover_image}
                          alt={c.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon size={56} strokeWidth={1.2} className="text-[#D4B895]" />
                        </div>
                      )}
                      {/* Overlays */}
                      <div className="absolute top-3 left-3 flex gap-2">
                        {isPremium ? (
                          <span className="px-2.5 py-1 bg-[#E89B6C] text-[#0B192C] text-[10px] uppercase tracking-[0.18em] font-bold">
                            Premium
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 bg-[#0B7C5C] text-[#F9F8F6] text-[10px] uppercase tracking-[0.18em]">
                            Gratis
                          </span>
                        )}
                        {completed && (
                          <span data-testid={`badge-completed-${c.slug}`} className="inline-flex items-center gap-1 px-2 py-1 bg-[#0E7C8F] text-[#F9F8F6] text-[10px] uppercase tracking-[0.18em]">
                            <Award size={10} /> Fatto
                          </span>
                        )}
                      </div>
                      {isPremium && !hasAccess && (
                        <div className="absolute inset-0 bg-[#0B192C]/40 flex items-center justify-center">
                          <Lock size={40} className="text-[#F9F8F6]/90" />
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-serif-display text-xl leading-tight text-[#0B192C]">
                        {c.title}
                      </h3>
                      <p className="mt-2 text-sm font-light text-[#3A4F6B] line-clamp-2">
                        {c.subtitle}
                      </p>

                      {pct > 0 && hasAccess && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B] mb-1.5">
                            <span>Progresso</span>
                            <span className="font-mono">{pct}%</span>
                          </div>
                          <div className="h-1 bg-[#E5E1DA] relative overflow-hidden">
                            <div
                              className="absolute inset-y-0 left-0 bg-[#E89B6C] transition-all"
                              style={{ width: `${pct}%` }}
                              data-testid={`progress-bar-${c.slug}`}
                            />
                          </div>
                        </div>
                      )}

                      <div className="mt-4 flex items-center gap-3 text-xs text-[#5A6472] flex-wrap">
                        <span className="inline-flex items-center gap-1"><FileText size={12}/>{c.modules_count} mod</span>
                        <span className="inline-flex items-center gap-1"><PlayCircle size={12}/>{c.lessons_count} lez</span>
                        {c.duration_min > 0 && (
                          <span className="inline-flex items-center gap-1">~ {Math.round(c.duration_min/60)}h {c.duration_min%60}m</span>
                        )}
                      </div>

                      {c.tags && c.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {c.tags.slice(0, 3).map((t) => (
                            <span key={t} className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 bg-[#F4EDE0] text-[#0B192C]">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-auto pt-6 flex items-center justify-between">
                        {isPremium ? (
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.15em] text-[#5A6472]">
                              {hasAccess ? "Il tuo accesso" : "Corso Premium"}
                            </p>
                            {hasAccess ? (
                              <p className="text-sm font-semibold text-[#0B7C5C]">Attivo</p>
                            ) : (
                              <p className="text-2xl font-mono font-bold text-[#0B192C]">€ {Number(c.price).toFixed(0)}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs uppercase tracking-[0.15em] text-[#0B7C5C] font-semibold">
                            Incluso
                          </p>
                        )}
                        {clickable && (
                          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-[#E89B6C] group-hover:gap-2 transition-all">
                            {isPremium && !hasAccess ? "Scopri" : pct === 0 ? "Inizia" : completed ? "Rivedi" : "Continua"} <ArrowRight size={12} />
                          </span>
                        )}
                      </div>
                      {!isAvail && (
                        <p className="mt-4 inline-block px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] bg-[#F4E8D6] text-[#0B192C]">
                          In preparazione
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function SubscriptionBanner({ courses }) {
  const premium = courses.filter((c) => c.is_premium);
  const withAccess = premium.filter((c) => c.has_access);
  if (premium.length === 0) return null;
  // If user already has access to ALL premium courses, hide banner
  if (withAccess.length === premium.length) return null;

  const subscribe = async () => {
    try {
      const { data } = await axios.post(
        `${API}/member/subscription/checkout`,
        { origin_url: window.location.origin },
        { headers: memberHdr() }
      );
      if (data?.url) window.location.href = data.url;
      else alert("Errore avvio checkout.");
    } catch (err) {
      alert(err?.response?.data?.detail || "Errore checkout abbonamento");
    }
  };

  return (
    <div className="mb-8 bg-gradient-to-r from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6] p-6 md:p-8 relative overflow-hidden" data-testid="subscription-banner">
      <div className="absolute top-0 right-0 opacity-10 pointer-events-none">
        <Sparkles size={200} strokeWidth={0.5} />
      </div>
      <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-[#E89B6C]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#E89B6C] font-bold">All-Access Mensile</span>
          </div>
          <h3 className="font-serif-display text-2xl md:text-3xl mb-2">
            Sblocca tutti i {premium.length} corsi Premium
          </h3>
          <p className="text-sm text-[#F9F8F6]/80 max-w-2xl">
            Un mese di accesso illimitato a Cybersecurity, Personal Branding L.4/2013, Vendita Consulenziale, Leadership Digitale 4.0. Cancella quando vuoi.
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#F9F8F6]/70">Solo</p>
          <p className="text-4xl font-mono font-bold text-[#E89B6C]">€ 29,90</p>
          <p className="text-[10px] text-[#F9F8F6]/60">al mese · 30 giorni</p>
          <button
            onClick={subscribe}
            data-testid="subscribe-btn"
            className="mt-3 inline-flex items-center gap-2 px-6 py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] font-bold w-full justify-center"
          >
            Paga con carta
          </button>
          <div className="mt-2" style={{ minWidth: 240 }}>
            <PayPalPayButton
              purchaseType="subscription_month"
              description="Metodo di Mare Academy — Accesso mensile"
              authToken={getMemberToken()}
              onSuccess={(res) => {
                if (res?.completed) {
                  alert("✓ Pagamento PayPal riuscito! Accesso a tutti i corsi Premium attivato.");
                  window.location.reload();
                } else {
                  alert("Pagamento non completato: " + (res?.status || "unknown"));
                }
              }}
              onError={(err) => alert("Errore PayPal: " + (err?.message || err))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// COURSE DETAIL — modules + lessons
// ============================================================

function CourseDetailView({ slug, onBack }) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeLesson, setActiveLesson] = useState({ mi: 0, li: 0 });
  const [progress, setProgress] = useState({ completed_lessons: [], percent: 0, completed_at: null });
  const [reviewModal, setReviewModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/member/courses/${slug}`, { headers: memberHdr() }),
          axios.get(`${API}/member/progress`, { headers: memberHdr() }),
        ]);
        setCourse(c.data);
        const myProg = p.data.progress?.[slug] || { completed_lessons: [], percent: 0 };
        setProgress(myProg);
        // Resume at last lesson if any
        if (myProg.last_lesson) {
          const [mi, li] = myProg.last_lesson.split("_").map(Number);
          setActiveLesson({ mi, li });
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Errore di caricamento");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const completedSet = new Set(progress.completed_lessons || []);
  const isLessonDone = (mi, li) => completedSet.has(`${mi}_${li}`);

  const toggleLesson = async (mi, li, completed) => {
    try {
      const { data } = await axios.post(
        `${API}/member/progress/mark`,
        { slug, mi, li, completed },
        { headers: memberHdr() }
      );
      setProgress({
        completed_lessons: data.completed_lessons,
        percent: data.percent,
        completed_at: data.completed_at,
      });
      if (data.just_completed) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 5000);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || "Errore");
    }
  };

  const downloadCertificate = () => {
    const token = getMemberToken();
    // Open in new tab carrying token via authorization is not possible with <a>,
    // so we fetch as blob.
    fetch(`${API}/member/courses/${slug}/certificate`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error("Errore download attestato");
        return r.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `attestato_${slug}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(err => alert(err.message));
  };

  if (loading)
    return (
      <section className="py-32 flex justify-center">
        <Loader2 className="animate-spin text-[#0B192C]" size={28} />
      </section>
    );
  if (!course)
    return (
      <section className="py-32 text-center">
        <p className="text-sm text-red-700">{error}</p>
        <button
          onClick={onBack}
          className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C]"
        >
          <ArrowLeft size={14} /> Torna ai corsi
        </button>
      </section>
    );

  const Icon = ICONS[course.slug] || BookOpen;
  const currentModule = course.modules?.[activeLesson.mi];
  const currentLesson = currentModule?.lessons?.[activeLesson.li];
  const lessonDone = isLessonDone(activeLesson.mi, activeLesson.li);
  const completed = progress.percent === 100;
  const canReview = progress.percent >= 50;
  const isPremium = course.is_premium;
  const hasAccess = course.has_access !== false;

  const requestPurchase = async () => {
    // Try real Stripe checkout first; fall back to purchase intent if Stripe not configured.
    try {
      const { data } = await axios.post(
        `${API}/member/courses/${slug}/checkout`,
        { origin_url: window.location.origin },
        { headers: memberHdr() }
      );
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (err) {
      // If Stripe error, fall back to intent
      try {
        await axios.post(
          `${API}/member/courses/purchase-intent`,
          { course_slug: slug },
          { headers: memberHdr() }
        );
        alert(`✓ Richiesta inviata!\n\nRiceverai a breve un'email con il link di pagamento per "${course.title}" a € ${Number(course.price).toFixed(2)}.\nDopo il pagamento l'accesso sarà attivato automaticamente.`);
      } catch (err2) {
        alert(err2?.response?.data?.detail || err?.response?.data?.detail || "Errore invio richiesta");
      }
    }
  };

  return (
    <>
      {/* Celebration toast */}
      {showCelebration && (
        <div data-testid="completion-toast" className="fixed top-20 right-6 z-50 bg-[#E89B6C] text-[#0B192C] px-6 py-4 shadow-xl border-2 border-[#0B192C] animate-in slide-in-from-right">
          <div className="flex items-center gap-3">
            <Award size={24} />
            <div>
              <p className="font-serif-display text-lg leading-tight">Corso completato!</p>
              <p className="text-xs">Scarica il tuo attestato 🎓</p>
            </div>
          </div>
        </div>
      )}

      {/* Course header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6]">
        {course.hero_image && (
          <div className="absolute inset-0 opacity-25">
            <img src={course.hero_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C] via-transparent to-[#0B192C]/60" />
          </div>
        )}
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 py-12 md:py-16">
          <button
            onClick={onBack}
            data-testid="course-back-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#E89B6C] hover:text-[#F9F8F6] mb-6"
          >
            <ArrowLeft size={14} /> Tutti i corsi
          </button>
          <div className="flex items-start gap-4 flex-wrap">
            {isPremium && (
              <span className="px-3 py-1.5 bg-[#E89B6C] text-[#0B192C] text-[10px] uppercase tracking-[0.2em] font-bold">
                Premium · € {Number(course.price).toFixed(0)}
              </span>
            )}
            {course.tags?.map((t) => (
              <span key={t} className="px-2 py-1 border border-[#F9F8F6]/30 text-[10px] uppercase tracking-[0.15em]">{t}</span>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-4">
            <Icon size={36} strokeWidth={1.2} className="text-[#E89B6C] mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h1 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight">
                {course.title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm md:text-base font-light text-[#F9F8F6]/80">
                {course.subtitle}
              </p>
              {course.description && (
                <p className="mt-4 max-w-3xl text-sm font-light text-[#F9F8F6]/70 leading-relaxed">
                  {course.description}
                </p>
              )}
            </div>
          </div>

          {isPremium && !hasAccess ? (
            <div className="mt-8 max-w-2xl bg-[#F9F8F6]/10 backdrop-blur-md border border-[#E89B6C]/40 p-6" data-testid="premium-locked-cta">
              <div className="flex items-center gap-3 mb-3">
                <Lock className="text-[#E89B6C]" size={20} />
                <p className="text-xs uppercase tracking-[0.2em] text-[#E89B6C] font-bold">Corso Premium bloccato</p>
              </div>
              <p className="text-sm text-[#F9F8F6]/90 mb-4">
                Sblocca l&apos;accesso completo a <strong>{course.modules?.length || 0} moduli</strong> e{" "}
                <strong>{course.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0} lezioni</strong>{" "}
                per <strong>{course.access_period_days || 365} giorni</strong>.
              </p>
              <button
                onClick={requestPurchase}
                data-testid="purchase-course-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-colors font-bold"
              >
                Sblocca a € {Number(course.price).toFixed(2)} <ArrowRight size={14} />
              </button>
              <div className="mt-4 max-w-xs">
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#F9F8F6]/60 mb-2">Oppure paga con PayPal</p>
                <PayPalPayButton
                  purchaseType="course"
                  courseSlug={slug}
                  description={`Corso: ${course.title}`}
                  authToken={getMemberToken()}
                  onSuccess={(res) => {
                    if (res?.completed) {
                      alert("✓ Pagamento PayPal riuscito! Accesso al corso attivato.");
                      window.location.reload();
                    } else {
                      alert("Pagamento non completato: " + (res?.status || "unknown"));
                    }
                  }}
                  onError={(err) => alert("Errore PayPal: " + (err?.message || err))}
                />
              </div>
              <p className="text-[10px] text-[#F9F8F6]/60 mt-3">
                Riceverai email con link di pagamento sicuro. Accesso attivato entro 24h dal pagamento.
              </p>
            </div>
          ) : (
            /* Progress bar (only for accessed courses) */
            <div className="mt-8 max-w-md">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#F9F8F6]/70 mb-1.5">
                <span>Il tuo progresso</span>
                <span className="font-mono text-[#E89B6C]">{progress.percent || 0}%</span>
              </div>
              <div className="h-1.5 bg-[#F9F8F6]/15 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[#E89B6C] transition-all"
                  style={{ width: `${progress.percent || 0}%` }}
                  data-testid="course-progress-bar"
                />
              </div>
            </div>
          )}

          {completed && (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={downloadCertificate}
                data-testid="download-certificate-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-colors"
              >
                <Award size={14} /> Scarica attestato PDF
              </button>
              <button
                onClick={() => setReviewModal(true)}
                data-testid="open-review-modal-btn"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#E89B6C] text-[#E89B6C] text-xs uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] transition-colors"
              >
                <Star size={14} /> Lascia recensione
              </button>
            </div>
          )}

          {!completed && canReview && hasAccess && (
            <button
              onClick={() => setReviewModal(true)}
              data-testid="open-review-early-btn"
              className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#E89B6C] hover:text-[#F9F8F6] link-underline"
            >
              <Star size={12} /> Lascia una recensione
            </button>
          )}
        </div>
      </section>

      {/* Course body: sidebar + lesson content */}
      <section className="py-12 md:py-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid lg:grid-cols-[320px,1fr] gap-8">
          {/* Sidebar */}
          <aside className="border border-[#E5E1DA] bg-white max-h-[calc(100vh-200px)] overflow-y-auto self-start lg:sticky lg:top-6" data-testid="course-sidebar">
            <div className="p-4 border-b border-[#E5E1DA] bg-[#F4E8D6]/40">
              <p className="overline">Programma</p>
              <p className="text-xs text-[#3A4F6B] mt-1">
                {course.modules?.length || 0} moduli ·{" "}
                {course.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0} lezioni
              </p>
            </div>
            {(course.modules || []).map((m, mi) => (
              <div key={mi} className="border-b border-[#E5E1DA] last:border-b-0">
                <div className="p-3 bg-[#F9F8F6]">
                  <p className="overline text-[10px] text-[#3A4F6B]">Modulo {m.n}</p>
                  <p className="text-sm font-medium text-[#0B192C] mt-0.5">{m.title}</p>
                </div>
                <ul className="divide-y divide-[#E5E1DA]">
                  {(m.lessons || []).map((l, li) => {
                    const active = activeLesson.mi === mi && activeLesson.li === li;
                    const done = isLessonDone(mi, li);
                    return (
                      <li key={li}>
                        <button
                          onClick={() => setActiveLesson({ mi, li })}
                          data-testid={`lesson-link-${mi}-${li}`}
                          className={`w-full text-left p-3 text-xs flex items-start gap-2 transition-colors ${
                            active ? "bg-[#E89B6C]/10 border-l-2 border-[#E89B6C]" : "hover:bg-[#F4E8D6]/40"
                          }`}
                        >
                          {done ? (
                            <CheckCircle2 size={14} className="text-[#0E7C8F] flex-shrink-0 mt-0.5" />
                          ) : (
                            <Circle size={14} className={active ? "text-[#E89B6C] flex-shrink-0 mt-0.5" : "text-[#3A4F6B] flex-shrink-0 mt-0.5"} />
                          )}
                          <span className={`flex-1 ${active ? "text-[#0B192C] font-medium" : done ? "text-[#3A4F6B] font-light line-through" : "text-[#0B192C] font-light"}`}>
                            {l.title}
                          </span>
                          {l.video_url && <Video size={11} className="text-[#0E7C8F] flex-shrink-0 mt-0.5" />}
                          {l.pdf_url && <FileText size={11} className="text-[#E89B6C] flex-shrink-0 mt-0.5" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </aside>

          {/* Lesson content */}
          <div data-testid="lesson-content" className="bg-white border border-[#E5E1DA] p-6 md:p-10">
            {currentLesson ? (
              <>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="overline">
                      Modulo {currentModule.n} · Lezione {activeLesson.li + 1}
                    </p>
                    <h2 className="font-serif-display text-2xl md:text-3xl mt-2 leading-tight text-[#0B192C]">
                      {currentLesson.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => toggleLesson(activeLesson.mi, activeLesson.li, !lessonDone)}
                    data-testid="mark-complete-btn"
                    className={`inline-flex items-center gap-2 px-4 py-2 border text-[10px] uppercase tracking-[0.18em] transition-all ${
                      lessonDone
                        ? "bg-[#0E7C8F] border-[#0E7C8F] text-white hover:bg-[#0E7C8F]/90"
                        : "border-[#0E7C8F] text-[#0E7C8F] hover:bg-[#0E7C8F] hover:text-white"
                    }`}
                  >
                    {lessonDone ? <><CheckCircle2 size={12} /> Completata</> : <><Circle size={12} /> Marca completata</>}
                  </button>
                </div>
                {currentModule.intro && activeLesson.li === 0 && (
                  <p className="mt-4 text-sm font-light text-[#3A4F6B] italic border-l-2 border-[#E89B6C] pl-4">
                    {currentModule.intro}
                  </p>
                )}

                {currentLesson.image_url && (
                  <div className="mt-6 relative overflow-hidden bg-[#F4EDE0]">
                    <img
                      src={currentLesson.image_url}
                      alt={currentLesson.title}
                      className="w-full h-auto"
                      onError={(e) => { e.target.parentElement.style.display = "none"; }}
                    />
                  </div>
                )}

                {currentLesson.video_url && (
                  <div className="mt-6 aspect-video bg-black">
                    <iframe
                      src={currentLesson.video_url}
                      title={currentLesson.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="lesson-video"
                    />
                  </div>
                )}

                {isPremium && !hasAccess ? (
                  <div className="mt-8 p-8 bg-[#F4EDE0] border-l-4 border-[#E89B6C] text-center" data-testid="lesson-locked">
                    <Lock className="inline text-[#E89B6C] mb-3" size={32} />
                    <p className="font-serif-display text-xl mb-2 text-[#0B192C]">Contenuto Premium bloccato</p>
                    <p className="text-sm text-[#5A6472] mb-4">
                      Sblocca questa lezione (e tutte le altre) acquistando il corso.
                    </p>
                    <button
                      onClick={requestPurchase}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#0B192C] hover:text-[#F9F8F6] transition-colors font-bold"
                    >
                      Sblocca a € {Number(course.price).toFixed(2)} <ArrowRight size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="mt-6 prose-mdm">
                    <Markdown text={currentLesson.content || ""} />
                  </div>
                )}

                {currentLesson.duration_min > 0 && (
                  <p className="mt-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#5A6472]">
                    <PlayCircle size={12} /> Durata stimata: {currentLesson.duration_min} min
                  </p>
                )}

                {currentLesson.pdf_url && (
                  <a
                    href={currentLesson.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="lesson-pdf-link"
                    className="mt-8 inline-flex items-center gap-2 px-5 py-3 border border-[#E89B6C] text-[#E89B6C] text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] hover:text-white transition-colors"
                  >
                    <Download size={13} /> Scarica PDF lezione
                  </a>
                )}

                {/* Nav */}
                <div className="mt-12 pt-6 border-t border-[#E5E1DA] flex items-center justify-between">
                  <button
                    onClick={() => {
                      let { mi, li } = activeLesson;
                      if (li > 0) li--;
                      else if (mi > 0) {
                        mi--;
                        li = (course.modules[mi].lessons.length || 1) - 1;
                      } else return;
                      setActiveLesson({ mi, li });
                    }}
                    data-testid="prev-lesson-btn"
                    disabled={activeLesson.mi === 0 && activeLesson.li === 0}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C] disabled:opacity-30"
                  >
                    <ArrowLeft size={13} /> Precedente
                  </button>
                  <button
                    onClick={() => {
                      // Auto-mark current as complete when moving forward
                      if (!lessonDone) toggleLesson(activeLesson.mi, activeLesson.li, true);
                      let { mi, li } = activeLesson;
                      const lessons = course.modules[mi].lessons || [];
                      if (li + 1 < lessons.length) li++;
                      else if (mi + 1 < course.modules.length) {
                        mi++;
                        li = 0;
                      } else return;
                      setActiveLesson({ mi, li });
                    }}
                    data-testid="next-lesson-btn"
                    disabled={
                      activeLesson.mi === course.modules.length - 1 &&
                      activeLesson.li === (currentModule.lessons?.length || 1) - 1 &&
                      lessonDone
                    }
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C] disabled:opacity-30"
                  >
                    Prossima <ArrowRight size={13} />
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-[#3A4F6B]">Nessuna lezione disponibile.</p>
            )}
          </div>
        </div>
      </section>

      {reviewModal && (
        <ReviewModal
          slug={slug}
          courseTitle={course.title}
          onClose={() => setReviewModal(false)}
        />
      )}
    </>
  );
}

function ReviewModal({ slug, courseTitle, onClose }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/member/courses/${slug}/my-review`, { headers: memberHdr() });
        if (data.review) {
          setExisting(data.review);
          setRating(data.review.rating);
          setComment(data.review.comment || "");
        }
      } catch {
        /* silent */
      }
    })();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/member/courses/${slug}/review`,
        { rating, comment },
        { headers: memberHdr() }
      );
      setSubmitted(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B192C]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#3A4F6B] hover:text-[#0B192C]" data-testid="review-modal-close">
          <X size={20} />
        </button>
        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle2 size={48} className="text-[#0E7C8F] mx-auto mb-4" />
            <p className="font-serif-display text-2xl text-[#0B192C]">Grazie per la tua recensione!</p>
            <p className="text-sm font-light text-[#3A4F6B] mt-2">Il tuo feedback aiuta tantissimo.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <p className="overline text-[#E89B6C]">{existing ? "Aggiorna recensione" : "Lascia recensione"}</p>
              <h3 className="font-serif-display text-2xl text-[#0B192C] mt-2 leading-tight">
                {courseTitle}
              </h3>
            </div>
            <div>
              <label className="overline block mb-3 text-[#3A4F6B]">La tua valutazione</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`star-${n}`}
                    className="p-1"
                  >
                    <Star
                      size={32}
                      fill={(hover || rating) >= n ? "#E89B6C" : "transparent"}
                      stroke={(hover || rating) >= n ? "#E89B6C" : "#3A4F6B"}
                      className="transition-all hover:scale-110"
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="overline block mb-2 text-[#3A4F6B]">Commento (opzionale)</label>
              <textarea
                rows={4}
                value={comment}
                maxLength={600}
                onChange={(e) => setComment(e.target.value)}
                data-testid="review-comment-input"
                placeholder="Cosa hai apprezzato di più? Cosa ti è servito?"
                className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm font-light focus:outline-none focus:border-[#0B192C]"
              />
              <p className="text-[10px] text-[#3A4F6B] mt-1">{comment.length}/600</p>
            </div>
            {error && <p className="text-sm text-red-700">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="review-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] hover:text-[#0B192C] transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />}
              {existing ? "Aggiorna" : "Invia"} recensione
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Lightweight markdown renderer (paragraphs, bold, italic, headings, lists)
// ============================================================

function Markdown({ text }) {
  if (!text) return null;
  const blocks = text.split(/\n\s*\n/).filter(Boolean);
  return (
    <>
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        // Heading
        if (trimmed.startsWith("# ")) {
          return (
            <h3 key={i} className="font-serif-display text-2xl text-[#0B192C] mt-6 mb-3">
              {inline(trimmed.slice(2))}
            </h3>
          );
        }
        if (trimmed.startsWith("## ")) {
          return (
            <h4 key={i} className="font-serif-display text-xl text-[#0B192C] mt-5 mb-2">
              {inline(trimmed.slice(3))}
            </h4>
          );
        }
        // List
        if (/^\s*[-*]\s/.test(trimmed)) {
          const items = trimmed.split("\n").map((l) => l.replace(/^\s*[-*]\s/, ""));
          return (
            <ul key={i} className="my-3 space-y-2 list-disc pl-6 text-[15px] leading-relaxed">
              {items.map((it, k) => (
                <li key={k}>{inline(it)}</li>
              ))}
            </ul>
          );
        }
        // Paragraph
        return (
          <p key={i} className="my-3 text-[15px] leading-relaxed text-[#0B192C] font-light">
            {inline(trimmed)}
          </p>
        );
      })}
    </>
  );
}

function inline(s) {
  // Process **bold** then *italic* then `code`
  const parts = [];
  let rest = s;
  let key = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/;
  while (true) {
    const m = rest.match(re);
    if (!m) {
      parts.push(rest);
      break;
    }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[2]) parts.push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={key++}>{m[3]}</em>);
    else if (m[4])
      parts.push(
        <code key={key++} className="font-mono text-[13px] bg-[#F4E8D6]/40 px-1 py-0.5">
          {m[4]}
        </code>
      );
    else if (m[5] && m[6])
      parts.push(
        <a key={key++} href={m[6]} target="_blank" rel="noreferrer" className="text-[#0E7C8F] underline">
          {m[5]}
        </a>
      );
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
}
